import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, UserPlus, X, Check, Building2, GraduationCap, FileText, Download, Eye, Send, CheckCircle, Clock, XCircle, Upload, Trash2, FolderOpen, ChevronDown, ChevronUp, MessageSquare, Phone, Mail, Search, Camera, User } from 'lucide-react';
import { toast } from 'sonner';
import { openBlobInNewTab, base64ToBlob } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import LettreMissionForm from '../components/LettreMissionForm';
import RoleBadges from '../components/RoleBadges';
import { getAuthPhotoUrl } from '../components/AuthImage';
import { ConformiteSousTraitant } from '../components/formateurs/ConformiteSousTraitant';

const GestionFormateurs = ({ user }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [formateurs, setFormateurs] = useState([]);
  const [stagiaires, setStagiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFormateur, setExpandedFormateur] = useState(null);
  const [assignMode, setAssignMode] = useState(null);
  const [showLettreMission, setShowLettreMission] = useState(null);
  const [centreProfile, setCentreProfile] = useState(null);
  const [lettresMission, setLettresMission] = useState([]);
  const [documentsFormateur, setDocumentsFormateur] = useState({});
  const [personalDocs, setPersonalDocs] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(null);
  const [uploadData, setUploadData] = useState({ titre: '', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { loadData(); loadCentreProfile(); loadLettresMission(); }, []);

  const loadCentreProfile = async () => {
    try {
      const response = await axios.get('/centre/profiles');
      if (response.data?.sites?.length > 0) setCentreProfile(response.data.sites[0]);
    } catch (error) { console.error('Error:', error); }
  };
  const loadLettresMission = async () => {
    try {
      const response = await axios.get('/lettres-mission');
      setLettresMission(response.data || []);
      await axios.put('/notifications/mark-read-by-type/lettre_mission').catch(() => {});
    } catch (error) { console.error('Error:', error); }
  };
  const loadData = async () => {
    setLoading(true);
    try {
      const [formateursRes, stagiairesRes] = await Promise.all([
        axios.get('/formateurs'), axios.get('/stagiaires')
      ]);
      setFormateurs(formateursRes.data || []);
      const mappedStagiaires = (stagiairesRes.data || []).map(s => ({
        ...s, prospect_id: s.prospect_id || s.user_id,
      }));
      setStagiaires(mappedStagiaires);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };
  const loadDocuments = async (formateurId) => {
    try {
      const [centreDocsRes, personalDocsRes] = await Promise.all([
        axios.get(`/documents-formateur/${formateurId}`),
        axios.get(`/documents/${formateurId}`).catch(() => ({ data: [] }))
      ]);
      setDocumentsFormateur(prev => ({ ...prev, [formateurId]: centreDocsRes.data || [] }));
      setPersonalDocs(prev => ({ ...prev, [formateurId]: personalDocsRes.data || [] }));
    } catch {
      setDocumentsFormateur(prev => ({ ...prev, [formateurId]: [] }));
      setPersonalDocs(prev => ({ ...prev, [formateurId]: [] }));
    }
  };
  const toggleFormateur = (formateurId) => {
    if (expandedFormateur === formateurId) {
      setExpandedFormateur(null);
      setAssignMode(null);
    } else {
      setExpandedFormateur(formateurId);
      setAssignMode(null);
      if (!documentsFormateur[formateurId]) loadDocuments(formateurId);
    }
  };

  // Actions
  const handleAssignClasse = async (formateurId, centre) => {
    try { await axios.post(`/formateurs/${formateurId}/assign-classe`, { centre }); toast.success(`Classe ${centre} assignee`); loadData(); setAssignMode(null); } catch { toast.error('Erreur'); }
  };
  const handleAssignStagiaire = async (formateurId, stagiaireId) => {
    try { await axios.post(`/formateurs/${formateurId}/assign-stagiaire`, { stagiaire_id: stagiaireId }); toast.success('Stagiaire assigne'); loadData(); } catch { toast.error('Erreur'); }
  };
  const handleRemoveStagiaire = async (formateurId, stagiaireId) => {
    try { await axios.delete(`/formateurs/${formateurId}/remove-stagiaire/${stagiaireId}`); toast.success('Stagiaire retire'); loadData(); } catch { toast.error('Erreur'); }
  };
  const handleDeleteFormateur = async (formateur) => {
    if (!window.confirm(`Supprimer ${formateur.prenom} ${formateur.nom} ?`)) return;
    try { await axios.delete(`/formateurs/${formateur.user_id}`); toast.success('Formateur supprime'); loadData(); } catch { toast.error('Erreur'); }
  };
  const handleSendLettre = async (lettreId) => {
    try { await axios.post(`/lettres-mission/${lettreId}/envoyer`); toast.success('Lettre envoyee'); loadLettresMission(); } catch { toast.error('Erreur'); }
  };
  const handleDeleteLettre = async (lettreId) => {
    if (!window.confirm('Supprimer cette lettre ?')) return;
    try { await axios.delete(`/lettres-mission/${lettreId}`); toast.success('Lettre supprimee'); loadLettresMission(); } catch { toast.error('Erreur'); }
  };
  const handleDownloadPdf = async (lettreId, formateurNom) => {
    try {
      const response = await axios.get(`/lettres-mission/${lettreId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `lettre_mission_${formateurNom}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Erreur'); }
  };
  const handleViewPdf = async (lettreId) => {
    try {
      const response = await axios.get(`/lettres-mission/${lettreId}/pdf`, { responseType: 'blob' });
      openBlobInNewTab(new Blob([response.data], { type: 'application/pdf' }));
    } catch { toast.error('Erreur'); }
  };
  const handleUploadDocument = async (formateurId) => {
    if (!uploadData.titre.trim()) { toast.error('Le titre est requis'); return; }
    setUploading(true);
    const formData = new FormData();
    if (selectedFile) formData.append('file', selectedFile);
    formData.append('formateur_id', formateurId);
    formData.append('titre', uploadData.titre);
    formData.append('description', uploadData.description || '');
    try {
      await axios.post('/documents-formateur/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(selectedFile ? 'Document envoye' : 'Message envoye');
      setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' });
      loadDocuments(formateurId);
    } catch { toast.error('Erreur'); }
    finally { setUploading(false); }
  };
  const handleViewDocument = async (doc) => {
    await previewDocument(doc.document_id, doc.filename, `/documents-formateur/${doc.document_id}/download`);
  };
  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`/documents-formateur/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', doc.filename);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Erreur'); }
  };
  const handleDeleteDocument = async (documentId, formateurId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await axios.delete(`/documents-formateur/${documentId}`); toast.success('Document supprime'); loadDocuments(formateurId); } catch { toast.error('Erreur'); }
  };
  const handleViewPersonalDoc = async (documentId) => {
    await previewDocument(documentId);
  };
  const handleDeletePersonalDoc = async (documentId, formateurId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try { await axios.delete(`/documents/${documentId}`); toast.success('Document supprime'); loadDocuments(formateurId); } catch { toast.error('Erreur suppression'); }
  };

  const getAvailableStagiaires = (formateur) => {
    const seen = new Set();
    return stagiaires.filter(s => { const sid = s.user_id; if (seen.has(sid)) return false; seen.add(sid); return true; });
  };
  const getUnassignedStagiaires = (formateur) => {
    const assigned = formateur.stagiaires_assignes || [];
    return getAvailableStagiaires(formateur).filter(s => !assigned.includes(s.user_id) && !assigned.includes(s.prospect_id));
  };
  const getAssignedStagiaires = (formateur) => {
    const ids = formateur.stagiaires_assignes || [];
    const seen = new Set();
    return stagiaires.filter(s => { const sid = s.user_id; if (seen.has(sid)) return false; const match = ids.includes(s.prospect_id) || ids.includes(s.user_id); if (match) seen.add(sid); return match; });
  };
  const getLettresFormateur = (formateurId) => lettresMission.filter(l => l.formateur_id === formateurId);
  const formatFileSize = (bytes) => { if (bytes < 1024) return bytes + ' o'; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko'; return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'; };
  const getStatusBadge = (statut) => {
    const m = { brouillon: { bg: 'bg-slate-100 text-slate-600', icon: Clock, label: 'Brouillon' }, envoyee: { bg: 'bg-blue-100 text-blue-700', icon: Send, label: 'Envoyee' }, acceptee: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Acceptee' }, refusee: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Refusee' } }[statut];
    if (!m) return null; const Icon = m.icon;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${m.bg} text-xs rounded-full`}><Icon className="h-3 w-3" />{m.label}</span>;
  };

  const filtered = formateurs.filter(f => `${f.prenom} ${f.nom} ${f.email}`.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div></div>;

  return (
    <div className="space-y-6" data-testid="gestion-formateurs-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Formateurs</h1>
        <p className="text-xs sm:text-sm text-slate-500">Profils, stagiaires et documents</p>
      </div>

      {/* Stats + Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 bg-gradient-to-r from-[#8DC63F]/10 to-transparent p-3 sm:p-4 rounded-xl border border-[#8DC63F]/20 flex-1">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#8DC63F]/20 rounded-full flex items-center justify-center flex-shrink-0">
            <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6 text-[#8DC63F]" />
          </div>
          <div>
            <span className="text-xl sm:text-2xl font-bold text-[#0F172A]">{formateurs.length}</span>
            <p className="text-xs sm:text-sm text-slate-500">Formateurs</p>
          </div>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-formateurs" />
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <GraduationCap className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{search ? 'Aucun formateur trouve' : 'Aucun formateur enregistre'}</p>
          </div>
        ) : filtered.map((formateur) => {
          const isExpanded = expandedFormateur === formateur.user_id;
          const assignedCount = (formateur.stagiaires_assignes || []).length;
          const lettresFormateur = getLettresFormateur(formateur.user_id);
          const docs = documentsFormateur[formateur.user_id] || [];
          const persDocs = personalDocs[formateur.user_id] || [];
          const fullName = `${formateur.prenom || ''} ${formateur.nom || ''}`.trim();
          const photo = formateur.profile?.photo || formateur.photo_identite;
          const photoUrl = photo ? getAuthPhotoUrl(photo) : null;
          const p = formateur.profile || {};

          return (
            <div key={formateur.user_id} className="bg-white border border-slate-200 rounded-xl hover:shadow-lg transition-all" data-testid={`formateur-card-${formateur.user_id}`}>
              {/* Header row */}
              <div className="p-3 sm:p-4 cursor-pointer hover:bg-slate-50" onClick={() => toggleFormateur(formateur.user_id)}>
                <div className="flex items-start sm:items-center justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-12 sm:w-14 sm:h-[4.5rem] rounded-md overflow-hidden bg-gradient-to-br from-[#8DC63F] to-green-700 flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                      {photoUrl ? (
                        <img src={photoUrl} alt={fullName} className="w-full h-full object-cover" data-testid={`formateur-photo-${formateur.user_id}`} />
                      ) : (
                        <>{(formateur.prenom?.[0] || '').toUpperCase()}{(formateur.nom?.[0] || '').toUpperCase()}</>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm sm:text-lg text-[#0F172A] truncate">{fullName}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-3 mt-0.5 text-xs sm:text-sm text-slate-500">
                        {formateur.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 flex-shrink-0" /> <span className="truncate">{formateur.email}</span></span>}
                        {formateur.telephone && <span className="flex items-center gap-1"><Phone className="h-3 w-3 flex-shrink-0" /> {formateur.telephone}</span>}
                      </div>
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        <RoleBadges userId={formateur.user_id} roles={formateur.roles || [formateur.role || 'formateur']} onRolesChange={(newRoles) => {
                          setFormateurs(prev => prev.map(f => f.user_id === formateur.user_id ? {...f, roles: newRoles} : f));
                        }} />
                        {formateur.centre && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${formateur.centre.toLowerCase() === 'atlantique' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                            {formateur.centre}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <div className="flex gap-1 flex-wrap justify-end">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] sm:text-xs font-medium rounded-full flex items-center gap-1">
                        <Users className="h-3 w-3" /> {assignedCount}
                      </span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] sm:text-xs font-medium rounded-full">
                        {lettresFormateur.length} lm
                      </span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] sm:text-xs font-medium rounded-full">
                        {(docs.length + persDocs.length)} doc
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteFormateur(formateur); }}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded: IDEM TUTEURS - 3 colonnes + section Centre pleine largeur */}
              {isExpanded && (
                <div className="border-t border-slate-200 bg-gradient-to-b from-green-50 to-white p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Col 1: Profil Complet */}
                    <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <User className="h-5 w-5" /> Profil Complet
                      </h4>
                      {/* Photo */}
                      <div className="flex justify-center mb-4">
                        <div className="w-20 h-28 rounded-md overflow-hidden bg-green-100 border-4 border-green-200">
                          {photoUrl ? <img src={photoUrl} alt={fullName} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-green-400"><Camera className="h-8 w-8" /></div>}
                        </div>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Genre</span>
                          <span className="font-medium">{p.genre || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Nom</span>
                          <span className="font-medium">{formateur.nom || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Prenom</span>
                          <span className="font-medium">{formateur.prenom || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Email</span>
                          <span className="font-medium text-xs">{formateur.email || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Telephone</span>
                          <span className="font-medium">{formateur.telephone || p.telephone || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Centre</span>
                          <span className="font-medium">{formateur.centre || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Diplome</span>
                          <span className="font-medium">{p.diplome || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Annee diplome</span>
                          <span className="font-medium">{p.annee_diplome || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Specialite</span>
                          <span className="font-medium">{p.specialite || '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Experience</span>
                          <span className="font-medium">{p.experience_annees ? `${p.experience_annees} ans` : '-'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-500">Competences</span>
                          <span className="font-medium text-xs text-right max-w-[60%]">{p.competences || '-'}</span>
                        </div>
                      </div>
                      {/* Signature */}
                      {p.signature && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm text-slate-500 mb-2">Signature</p>
                          <img src={p.signature} alt="Signature" className="max-h-16 border border-slate-200 rounded" />
                        </div>
                      )}
                    </div>

                    {/* Col 2: Stagiaires + Lettres de Mission */}
                    <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <Users className="h-5 w-5" /> Stagiaires & Lettres Mission
                      </h4>

                      {/* Stagiaires */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-slate-500 uppercase">Stagiaires ({getAssignedStagiaires(formateur).length})</p>
                          <div className="flex gap-1">
                            <button onClick={(e) => { e.stopPropagation(); setAssignMode(assignMode === 'classe' ? null : 'classe'); }} className={`px-2 py-0.5 rounded text-xs ${assignMode === 'classe' ? 'bg-[#8DC63F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <Building2 className="h-3 w-3 inline mr-0.5" />Classe
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setAssignMode(assignMode === 'individuel' ? null : 'individuel'); }} className={`px-2 py-0.5 rounded text-xs ${assignMode === 'individuel' ? 'bg-[#8DC63F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                              <UserPlus className="h-3 w-3 inline mr-0.5" />Ind.
                            </button>
                          </div>
                        </div>

                        {assignMode === 'classe' && (
                          <div className="flex gap-1 mb-2">
                            <Button size="sm" onClick={() => handleAssignClasse(formateur.user_id, 'atlantique')} className="h-6 text-xs bg-blue-500 hover:bg-blue-600">Atlantique</Button>
                            <Button size="sm" onClick={() => handleAssignClasse(formateur.user_id, 'occitanie')} className="h-6 text-xs bg-orange-500 hover:bg-orange-600">Occitanie</Button>
                          </div>
                        )}

                        {assignMode === 'individuel' && (
                          <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto divide-y divide-slate-100 mb-2">
                            {getUnassignedStagiaires(formateur).map(s => (
                              <div key={s.user_id} className="px-2 py-1.5 flex items-center justify-between">
                                <span className="text-xs">{s.user_info?.prenom || s.prenom} {s.user_info?.nom || s.nom}</span>
                                <button onClick={() => handleAssignStagiaire(formateur.user_id, s.user_id)} className="p-1 bg-[#8DC63F] text-white rounded"><Check className="h-3 w-3" /></button>
                              </div>
                            ))}
                            {getUnassignedStagiaires(formateur).length === 0 && <div className="p-2 text-center text-xs text-slate-400">Tous assignes</div>}
                          </div>
                        )}

                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {getAssignedStagiaires(formateur).length === 0 ? (
                            <div className="text-center py-3 bg-slate-50 rounded-lg"><p className="text-xs text-slate-400">Aucun stagiaire assigne</p></div>
                          ) : getAssignedStagiaires(formateur).map(s => {
                            const sn = s.user_info?.nom || s.nom || '';
                            const sp = s.user_info?.prenom || s.prenom || '';
                            const sc = s.user_info?.centre || s.centre || '';
                            return (
                              <div key={s.user_id} className="flex items-center justify-between p-1.5 bg-slate-50 rounded">
                                <div>
                                  <span className="text-xs font-medium">{sp} {sn}</span>
                                  {sc && <span className={`ml-1 text-[10px] px-1 py-0.5 rounded ${sc.toLowerCase() === 'atlantique' ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>{sc}</span>}
                                </div>
                                <button onClick={() => handleRemoveStagiaire(formateur.user_id, s.user_id)} className="text-red-400 hover:text-red-600 p-0.5"><X className="h-3 w-3" /></button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Lettres de Mission */}
                      <div className="border-t border-slate-200 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase">Lettres de Mission ({lettresFormateur.length})</p>
                          <Button size="sm" onClick={(e) => { e.stopPropagation(); setShowLettreMission(formateur.user_id); }} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs text-white" data-testid={`btn-lettre-${formateur.user_id}`}>
                            <FileText className="h-3 w-3 mr-1" /> Creer
                          </Button>
                        </div>
                        {lettresFormateur.length === 0 ? (
                          <div className="text-center py-4 bg-slate-50 rounded-lg">
                            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-400 text-sm">Aucune lettre</p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {lettresFormateur.map(lettre => (
                              <div key={lettre.lettre_id} className={`p-3 rounded-lg border ${
                                lettre.statut === 'acceptee' ? 'border-green-200 bg-green-50' :
                                lettre.statut === 'refusee' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'
                              }`}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-xs text-[#0F172A] truncate">{lettre.intitule_formation}</p>
                                    <div className="flex flex-wrap items-center gap-1 mt-1">
                                      {getStatusBadge(lettre.statut)}
                                      {lettre.statut === 'acceptee' && lettre.date_signature_formateur && (() => {
                                        const diff = (new Date() - new Date(lettre.date_signature_formateur)) / 3600000;
                                        return diff < 48;
                                      })() && <span className="px-1.5 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full animate-pulse">Nouveau</span>}
                                    </div>
                                    <div className="flex gap-2 text-[10px] text-slate-500 mt-1">
                                      {lettre.dates && <span>{lettre.dates}</span>}
                                      {(lettre.montant_total || lettre.montant) && <span className="text-green-600 font-medium">{lettre.montant_total || lettre.montant} EUR</span>}
                                    </div>
                                    {lettre.statut === 'acceptee' && lettre.date_signature_formateur && (
                                      <p className="text-[10px] text-green-600 mt-0.5">Signee le {new Date(lettre.date_signature_formateur).toLocaleDateString('fr-FR')}</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <button onClick={(e) => { e.stopPropagation(); handleViewPdf(lettre.lettre_id); }} className="p-1.5 text-blue-500 hover:bg-blue-100 rounded" title="Voir"><Eye className="h-3 w-3" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDownloadPdf(lettre.lettre_id, `${lettre.formateur_nom}_${lettre.formateur_prenom}`); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Telecharger"><Download className="h-3 w-3" /></button>
                                    {lettre.statut === 'brouillon' && (
                                      <>
                                        <button onClick={(e) => { e.stopPropagation(); handleSendLettre(lettre.lettre_id); }} className="p-1.5 text-green-500 hover:bg-green-100 rounded" title="Envoyer"><Send className="h-3 w-3" /></button>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteLettre(lettre.lettre_id); }} className="p-1.5 text-red-500 hover:bg-red-100 rounded" title="Supprimer"><Trash2 className="h-3 w-3" /></button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <ConformiteSousTraitant formateurId={formateur.user_id} />
                    </div>

                    {/* Col 3: Documents du formateur */}
                    <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                      <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                        <FolderOpen className="h-5 w-5" /> Documents du formateur ({persDocs.length})
                      </h4>
                      {persDocs.length === 0 ? (
                        <div className="text-center py-8 bg-slate-50 rounded-lg">
                          <FolderOpen className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500 text-sm">Aucun document uploade</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {persDocs.map((doc) => (
                            <div key={doc.document_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-800 truncate max-w-[150px]">
                                  {(doc.document_type || doc.type || 'Document').replace(/_/g, ' ')}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button onClick={() => handleViewPersonalDoc(doc.document_id)} className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors" title="Voir"><Eye className="h-4 w-4" /></button>
                                <button onClick={() => handleDeletePersonalDoc(doc.document_id, formateur.user_id)} className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section pleine largeur: Documents et messages envoyes par le centre */}
                  <div className="mt-4 bg-white p-3 sm:p-5 rounded-xl border border-[#8DC63F]/30 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                      <h4 className="font-semibold text-[#0F172A] text-sm flex items-center gap-2">
                        <Send className="h-4 w-4 text-[#8DC63F] flex-shrink-0" />
                        <span className="truncate">Docs centre ({docs.length})</span>
                      </h4>
                      <Button size="sm" onClick={() => setShowUploadModal(formateur.user_id)} className="bg-[#8DC63F] hover:bg-[#7db336] text-xs whitespace-nowrap">
                        <Upload className="h-3.5 w-3.5 mr-1" /> Envoyer
                      </Button>
                    </div>
                    {docs.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Aucun document ou message envoye</p>
                    ) : (
                      <div className="space-y-2">
                        {docs.map(doc => (
                          <div key={doc.document_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-3">
                              {doc.type === 'message' ? <MessageSquare className="h-4 w-4 text-blue-500" /> : <FileText className="h-4 w-4 text-[#8DC63F]" />}
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-[#0F172A]">{doc.titre}</span>
                                  {doc.type === 'message' && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Message</span>}
                                </div>
                                {doc.description && <p className="text-xs text-slate-600">{doc.description}</p>}
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  {doc.filename && <span>{doc.filename}</span>}
                                  {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                                  <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                                  {doc.lu && <span className="text-green-600 font-medium">Lu</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {doc.type !== 'message' && (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleViewDocument(doc)}><Eye className="h-3 w-3" /></Button>
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleDownloadDocument(doc)}><Download className="h-3 w-3" /></Button>
                                </>
                              )}
                              <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200" onClick={() => handleDeleteDocument(doc.document_id, formateur.user_id)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal Lettre de Mission */}
      {showLettreMission && (() => {
        const f = formateurs.find(f => f.user_id === showLettreMission);
        return f ? (
          <LettreMissionForm
            formateur={f}
            centreProfile={centreProfile}
            user={user}
            onClose={() => setShowLettreMission(null)}
            onSuccess={() => { setShowLettreMission(null); loadLettresMission(); }}
          />
        ) : null;
      })()}

      {/* Modal Upload Document */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#0F172A]">Envoyer un document / message</h3>
              <button onClick={() => { setShowUploadModal(null); setSelectedFile(null); setUploadData({ titre: '', description: '' }); }} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Titre *</label>
                <input type="text" value={uploadData.titre} onChange={(e) => setUploadData({ ...uploadData, titre: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8DC63F] text-sm" placeholder="Ex: Convention de stage" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Message / Description</label>
                <textarea value={uploadData.description} onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none text-sm" placeholder="Votre message..." />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Fichier (optionnel)</label>
                <input type="file" ref={fileInputRef} onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} className="hidden" />
                <div onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#8DC63F]">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5 text-[#8DC63F]" />
                      <span className="text-sm">{selectedFile.name}</span>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if(fileInputRef.current) fileInputRef.current.value = ''; }} className="ml-1 text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div><Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" /><p className="text-sm text-slate-500">Cliquez pour selectionner</p></div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-3">
              <Button variant="outline" onClick={() => { setShowUploadModal(null); setSelectedFile(null); }} className="flex-1">Annuler</Button>
              <Button onClick={() => handleUploadDocument(showUploadModal)} disabled={uploading || !uploadData.titre.trim()} className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]" data-testid="send-doc-message-btn">
                {uploading ? 'Envoi...' : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
              </Button>
            </div>
          </div>
        </div>
      )}
      <DocumentModal />
    </div>
  );
};

export default GestionFormateurs;
